import React, { useState, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    Typography,
    Box,
    Divider,
    Grid,
    TextField,
    InputAdornment,
    IconButton,
} from "@mui/material";
import {
    Search,
    X,
    Download,
} from "lucide-react";

const EXPORT_FIELDS = [
    { key: "dealName", label: "Lead Name", group: "Basic Information" },
    { key: "contactName", label: "Customer Name", group: "Basic Information" },
    { key: "mobile", label: "Mobile Number", group: "Basic Information" },
    { key: "email", label: "Email Address", group: "Basic Information" },
    { key: "amount", label: "Deal Amount", group: "Lead Details" },
    { key: "stage", label: "Pipeline Stage", group: "Lead Details" },
    { key: "company", label: "Company Name", group: "Lead Details" },
    { key: "priority", label: "Priority Level", group: "Lead Details" },
    { key: "source", label: "Lead Source", group: "Lead Details" },
    { key: "assigneeName", label: "Assignee Name", group: "Lead Details" },
    { key: "city", label: "City", group: "Lead Details" },
    { key: "locationArea", label: "Location/Area", group: "Lead Details" },
    { key: "interest", label: "Interest Category", group: "Qualification" },
    { key: "subInterest", label: "Sub Category", group: "Qualification" },
    { key: "disposition", label: "Disposition", group: "Qualification" },
    { key: "subDisposition", label: "Sub Disposition", group: "Qualification" },
    { key: "createdAt", label: "Created Date", group: "Timeline" },
    { key: "closingDate", label: "Expected Closing", group: "Timeline" },
    { key: "followUpDate", label: "Follow-up Date", group: "Timeline" },
    { key: "notes", label: "Notes", group: "Notes & Remarks" },
    { key: "description", label: "Lead Description", group: "Notes & Remarks" },
    { key: "remarks", label: "Internal Remarks", group: "Notes & Remarks" },
    { key: "pocIdentified", label: "POC Status", group: "Point of Contact" },
    { key: "pocName", label: "POC Name", group: "Point of Contact" },
    { key: "pocPhone", label: "POC Phone", group: "Point of Contact" },
    { key: "pocEmail", label: "POC Email", group: "Point of Contact" },
    { key: "pocComments", label: "POC Comments", group: "Point of Contact" },
];

const ExportSelectionModal = ({ open, onClose, onExport }) => {
    const [selectedFields, setSelectedFields] = useState(
        EXPORT_FIELDS.filter(f => ["dealName", "contactName", "mobile", "amount", "stage", "createdAt", "notes", "assigneeName"].includes(f.key)).map(f => f.key)
    );
    const [searchQuery, setSearchQuery] = useState("");

    const filteredFields = useMemo(() => {
        return EXPORT_FIELDS.filter(field =>
            field.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            field.group.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    const handleToggle = (key) => {
        setSelectedFields((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const handleSelectAll = (isAll) => {
        if (isAll) {
            setSelectedFields(EXPORT_FIELDS.map((f) => f.key));
        } else {
            setSelectedFields([]);
        }
    };

    const handleExport = () => {
        onExport(selectedFields);
        onClose();
    };

    const groupedFields = useMemo(() => {
        return filteredFields.reduce((acc, field) => {
            if (!acc[field.group]) acc[field.group] = [];
            acc[field.group].push(field);
            return acc;
        }, {});
    }, [filteredFields]);

    const isAllSelected = selectedFields.length === EXPORT_FIELDS.length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                    border: "1px solid #e5e7eb",
                }
            }}
        >
            <DialogTitle sx={{ p: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                        Export Leads
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        Select the columns you want to include in your spreadsheet.
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: '#9ca3af' }}>
                    <X size={20} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, pt: 2, pb: 1, bgcolor: '#f9fafb' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search columns..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                bgcolor: 'white',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                "& fieldset": { borderColor: '#e5e7eb' },
                                "&:hover fieldset": { borderColor: '#d1d5db' },
                                "&.Mui-focused fieldset": { borderColor: '#6366f1', borderWidth: '1px' },
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size={16} color="#9ca3af" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Box sx={{ maxHeight: '400px', overflowY: 'auto', p: 3 }}>
                    {Object.keys(groupedFields).length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="body2" sx={{ color: '#9ca3af' }}>No columns found match your search.</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {Object.entries(groupedFields).map(([group, fields]) => (
                                <Box key={group}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {group}
                                        </Typography>
                                        <Box sx={{ flex: 1, height: '1px', bgcolor: '#f3f4f6' }} />
                                    </Box>
                                    <Grid container spacing={1}>
                                        {fields.map((field) => (
                                            <Grid item xs={12} key={field.key}>
                                                <Box
                                                    onClick={() => handleToggle(field.key)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        p: 1,
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.15s',
                                                        "&:hover": { bgcolor: '#f3f4f6' }
                                                    }}
                                                >
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedFields.includes(field.key)}
                                                        disableRipple
                                                        sx={{ p: 0, color: '#d1d5db', '&.Mui-checked': { color: '#6366f1' } }}
                                                    />
                                                    <Typography sx={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        color: selectedFields.includes(field.key) ? '#111827' : '#4b5563'
                                                    }}>
                                                        {field.label}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2.5, bgcolor: '#f9fafb', display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        onClick={() => handleSelectAll(!isAllSelected)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#4b5563',
                            bgcolor: 'white',
                            border: '1px solid #e5e7eb',
                            px: 1.5,
                            "&:hover": { bgcolor: '#f3f4f6', borderColor: '#d1d5db' }
                        }}
                    >
                        {isAllSelected ? "Deselect All" : "Select All"}
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        onClick={onClose}
                        sx={{ textTransform: 'none', fontSize: '0.875rem', fontWeight: 600, color: '#6b7280' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleExport}
                        disabled={selectedFields.length === 0}
                        disableElevation
                        startIcon={<Download size={16} />}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            borderRadius: '8px',
                            px: 3,
                            bgcolor: '#111827',
                            "&:hover": { bgcolor: '#000000' }
                        }}
                    >
                        Export Report
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ExportSelectionModal;
